import { motion, useReducedMotion } from 'framer-motion'

type Props = {
  badge: string
  stored: string
  b2c: string
  b2cDesc: string
  b2b: string
  b2bDesc: string
  carriers: string
}

const EASE = [0.22, 1, 0.36, 1] as const

// Branch paths (viewBox 0 0 600 300). Split hub at (212,150).
const B2C_PATH = 'M212 150 C 300 150, 320 84, 432 80'
const B2B_PATH = 'M212 150 C 300 150, 320 218, 432 222'

const BLUE = '#3b5dff'
const GREEN = '#0e9f6e'

export default function AdrSplitFlow({ badge, stored, b2c, b2cDesc, b2b, b2bDesc, carriers }: Props) {
  const reduce = useReducedMotion()

  const drawn = (delay: number) =>
    reduce
      ? { initial: { pathLength: 1 }, animate: { pathLength: 1 } }
      : { initial: { pathLength: 0 }, animate: { pathLength: 1 }, transition: { duration: 1.1, ease: EASE, delay } }

  const travel = (path: string, delay: number) =>
    reduce
      ? null
      : (
          <motion.g
            initial={{ offsetDistance: '0%', opacity: 0 }}
            animate={{ offsetDistance: ['0%', '8%', '92%', '100%'], opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2.4, ease: EASE, repeat: Infinity, repeatDelay: 1, delay, times: [0, 0.1, 0.88, 1] }}
            style={{ offsetPath: `path('${path}')`, offsetRotate: '0deg' }}
          >
            <rect x="-7" y="-7" width="14" height="14" rx="3" fill="#fff" stroke={BLUE} strokeWidth="2" />
            <path d="M-7 -1h14" stroke={BLUE} strokeWidth="1.4" />
          </motion.g>
        )

  return (
    <div className="relative w-full rounded-2xl sm:rounded-3xl border border-ink-100 bg-gradient-to-br from-white via-accent-50/30 to-white overflow-hidden isolate aspect-[4/3] sm:aspect-[16/10]">
      {/* dot pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.32]"
        style={{
          backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />
      {!reduce && (
        <motion.div
          aria-hidden
          className="absolute left-[8%] top-1/2 -translate-y-1/2 h-52 w-52 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.16), transparent 70%)' }}
          animate={{ scale: [1, 1.14, 1], opacity: [0.4, 0.65, 0.4] }}
          transition={{ duration: 10, ease: 'easeInOut', repeat: Infinity }}
        />
      )}

      {/* badge pill */}
      <div className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-accent-200 bg-accent-50/90 px-2.5 py-1 text-[10px] sm:text-xs text-accent-800 backdrop-blur-sm">
        <span className="relative flex h-1.5 w-1.5">
          {!reduce && (
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
          )}
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-600" />
        </span>
        <span className="font-medium">{badge}</span>
      </div>

      <svg viewBox="0 0 600 300" className="absolute inset-0 h-full w-full" aria-hidden style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
        {/* ── branches ── */}
        <motion.path d={B2C_PATH} fill="none" stroke={BLUE} strokeWidth="3" strokeLinecap="round" {...drawn(0.2)} />
        <motion.path d={B2B_PATH} fill="none" stroke={GREEN} strokeWidth="3" strokeLinecap="round" {...drawn(0.35)} />
        {/* split hub */}
        <circle cx="212" cy="150" r="7" fill="#fff" stroke="#2a3ff5" strokeWidth="3" />

        {/* travelling parcels */}
        {travel(B2C_PATH, 0.9)}
        {!reduce && (
          <motion.g
            initial={{ offsetDistance: '0%', opacity: 0 }}
            animate={{ offsetDistance: ['0%', '8%', '92%', '100%'], opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2.6, ease: EASE, repeat: Infinity, repeatDelay: 1, delay: 1.1, times: [0, 0.1, 0.88, 1] }}
            style={{ offsetPath: `path('${B2B_PATH}')`, offsetRotate: '0deg' }}
          >
            {/* small pallet stack */}
            <rect x="-9" y="-8" width="18" height="7" rx="1.5" fill="#fff" stroke={GREEN} strokeWidth="1.8" />
            <rect x="-9" y="0" width="18" height="7" rx="1.5" fill="#fff" stroke={GREEN} strokeWidth="1.8" />
          </motion.g>
        )}

        {/* ── source: ADR pallet ── */}
        <g transform="translate(78,150)">
          {/* box */}
          <rect x="-26" y="-34" width="52" height="42" rx="5" fill="#fff" stroke="#c7c7d0" strokeWidth="1.6" />
          {/* hazard diamond on the box */}
          <g transform="translate(0,-13)">
            <path d="M0 -13 L13 0 L0 13 L-13 0 Z" fill="#f59e0b" />
            <path d="M0 -5v6M0 6h.01" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
          </g>
          {/* pallet */}
          <rect x="-28" y="10" width="56" height="7" rx="1.5" fill="#d9b483" />
          <line x1="-22" y1="17" x2="-22" y2="24" stroke="#b98c52" strokeWidth="6" strokeLinecap="round" />
          <line x1="0" y1="17" x2="0" y2="24" stroke="#b98c52" strokeWidth="6" strokeLinecap="round" />
          <line x1="22" y1="17" x2="22" y2="24" stroke="#b98c52" strokeWidth="6" strokeLinecap="round" />
          <text x="0" y="44" textAnchor="middle" fontSize="13" fontWeight="600" fill="#363640">{stored}</text>
        </g>

        {/* ── B2C endpoint: van ── */}
        <g transform="translate(470,80)">
          <g stroke={BLUE} strokeWidth="2.4" fill="#fff" strokeLinejoin="round" strokeLinecap="round">
            <rect x="-34" y="-16" width="40" height="26" rx="3" />
            <path d="M6 -8h14l10 11v7H6z" />
            <circle cx="-20" cy="14" r="5.5" fill="#eef4ff" />
            <circle cx="18" cy="14" r="5.5" fill="#eef4ff" />
          </g>
        </g>
        {/* B2C labels */}
        <text x="250" y="74" fontSize="15" fontWeight="700" fill={BLUE}>{b2c}</text>
        <text x="250" y="92" fontSize="11.5" fill="#6b6b76">{b2cDesc}</text>

        {/* ── B2B endpoint: truck ── */}
        <g transform="translate(470,222)">
          <g stroke={GREEN} strokeWidth="2.4" fill="#fff" strokeLinejoin="round" strokeLinecap="round">
            <rect x="-40" y="-18" width="44" height="30" rx="3" />
            <path d="M4 -8h16l14 12v8H4z" />
            <circle cx="-24" cy="16" r="6" fill="#ecfdf5" />
            <circle cx="22" cy="16" r="6" fill="#ecfdf5" />
          </g>
        </g>
        {/* B2B labels */}
        <text x="250" y="214" fontSize="15" fontWeight="700" fill={GREEN}>{b2b}</text>
        <text x="250" y="232" fontSize="11.5" fill="#6b6b76">{b2bDesc}</text>
        <text x="470" y="262" textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#4f4f58">{carriers}</text>
      </svg>
    </div>
  )
}
